import { useRef, useState } from 'react';
import { sjisTruncate } from '../../../utils/format';

interface EditableFieldProps {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  isEdited?: boolean;
  autoFocus?: boolean;
  /** 上下キーで同じグループ内の前後セルへフォーカス移動する（例: 単価列の連続入力）。
   * 未指定なら上下キーは通常動作。 */
  navGroup?: string;
  /** 半角(ASCII印字可能文字)のみ許可し、全角・日本語の入力を除去する。
   * ERPが半角限定のコード/ID系項目(相手伝票No.=ADNID・連絡先=TELCM など)で使う。 */
  asciiOnly?: boolean;
  /** 入力可能な最大文字数。asciiOnly と併用するとバイト数=文字数になる。 */
  maxLength?: number;
  /** 入力可能な最大バイト数(Shift_JIS換算)。日本語を許すERP項目(相手伝票No.=ADNID)で使う。
   * バックエンドの切り詰めと同じ尺度なので、画面で収まっていれば無言で切られない。 */
  maxBytes?: number;
  /** 入力不可にする（値は表示するが編集できない） */
  disabled?: boolean;
}

/**
 * 常に input を表示。未フォーカス時はプレーンテキスト風、フォーカスで編集モード。
 * Tab で次のセルに自然に遷移する。navGroup 指定時は ↑/↓ で列内を上下移動。
 */
export function EditableField({
  value,
  onChange,
  className = '',
  isEdited,
  autoFocus,
  navGroup,
  asciiOnly,
  maxLength,
  maxBytes,
  disabled,
}: EditableFieldProps) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // asciiOnly 指定時は半角(印字可能ASCII)以外を除去し、maxLength で文字数を、
  // maxBytes で Shift_JIS 換算のバイト数を制限する。
  // 入力(タイプ/貼り付け)の段階で弾くため、上限を超えた値はそもそも入らない。
  const sanitize = (v: string) => {
    let out = asciiOnly ? v.replace(/[^\x20-\x7E]/g, '') : v;
    if (maxBytes != null) out = sjisTruncate(out, maxBytes);
    if (maxLength != null) out = out.slice(0, maxLength);
    return out;
  };

  // 外部からvalueが変わった場合にdraftを同期
  if (!focused && draft !== value) {
    setDraft(value);
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={focused ? draft : value}
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={(e) => setDraft(sanitize(e.target.value))}
      onFocus={() => {
        setDraft(value);
        setFocused(true);
        setTimeout(() => inputRef.current?.select(), 0);
      }}
      onBlur={() => {
        setFocused(false);
        if (draft !== value) onChange(draft);
      }}
      onKeyDown={(e) => {
        if (e.nativeEvent.isComposing) return;
        if (e.key === 'Enter') {
          e.preventDefault();
          if (draft !== value) onChange(draft);
          inputRef.current?.blur();
        }
        if (e.key === 'Escape') {
          setDraft(value);
          inputRef.current?.blur();
        }
        // navGroup 指定時は ↑/↓ で同じ列(グループ)の前後セルへフォーカス移動。
        // 現在の値は移動時の blur で確定される（削除行はinput自体が無く自動スキップ）。
        if (navGroup && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
          const cells = Array.from(
            document.querySelectorAll<HTMLInputElement>(`input[data-nav-group="${navGroup}"]`),
          );
          const idx = inputRef.current ? cells.indexOf(inputRef.current) : -1;
          const next = cells[idx + (e.key === 'ArrowDown' ? 1 : -1)];
          next?.focus();
        }
      }}
      data-nav-group={navGroup}
      maxLength={maxLength}
      title={value || undefined}
      className={`text-sm px-1 py-0.5 rounded outline-none ${
        disabled
          ? 'border border-slate-200 bg-slate-100 cursor-not-allowed'
          : focused
            ? 'border border-primary bg-white'
            : `border border-transparent bg-transparent cursor-pointer overflow-hidden text-ellipsis ${isEdited ? 'bg-amber-100 !border-amber-300' : ''}`
      } ${className}`}
    />
  );
}
